import type { Dispatch, SetStateAction } from 'react'

import ProductTextNodes from '@data/i18n/product.json'

import posthog from 'posthog-js'

import getGtmEcommerceProduct from '@lib/analytics/ecommerce'
import { gtmEvent } from '@lib/analytics/gtm'
import { pushAddToCart } from '@lib/analytics/elevar-data-layer'
import updateItem from '@lib/cart/update-item'
import removeItems from '@lib/cart/remove-items'
import useAddItem from '@lib/cart/use-add-item'
import useCart from '@context/useCartContext'
import type { Breadcrumbs } from '@lib/types'
import type { Product as ProductType, ProductVariant } from '@lib/types/product'
import { isCustomizableBoxVariant } from '@lib/utils/isSubscriptionBox'

type AsyncFunction = () => Promise<unknown>

export type AddToCartProps = {
	product: Partial<ProductType>
	variant: Partial<ProductVariant>
	loading: boolean
	setLoading: Dispatch<SetStateAction<boolean>>
	sellingPlanId: string | null
	priceText?: string
	ctaText?: string
	onAddToCart?: () => void
	className?: string
	icon?: string
	breadcrumbs?: Breadcrumbs | null
}

export default function AddToCart({
	product,
	variant,
	loading,
	setLoading,
	sellingPlanId,
	priceText,
	ctaText = ProductTextNodes.cart.add,
	onAddToCart,
	className = 'button button-primary whitespace-nowrap',
	icon,
	breadcrumbs = null
}: AddToCartProps) {
	const addItem = useAddItem()
	const isCustomizableBox = isCustomizableBoxVariant(variant?.id)
	const { items, revalidate } = useCart()

	const productCTAText = loading ? ProductTextNodes.cart.adding : ctaText

	// Add to cart function
	const addToCart = async () => {
		setLoading(true)

		const customizableBoxInCartLineId = items?.find((item) =>
			isCustomizableBoxVariant(item.merchandise.id)
		)?.id

		// Force an item to have a `sellingPlanId` if it requires a selling plan
		if (!sellingPlanId && product.requiresSellingPlan) {
			throw new Error('Product requires a selling plan, but none was selected.')
		}

		// If the product is a subscription box, we need to handle the cart differently
		const originalItemsArray: any[] = items || []

		// Filter the array based on sellingPlanAllocation
		const nonSubscriptionItems: any[] = originalItemsArray.filter(
			(item) =>
				!item.sellingPlanAllocation &&
				!item.merchandise.sku.startsWith('bb-box') &&
				!item.merchandise.sku.startsWith('signature-box')
		)
		const subscriptionItems: any[] = originalItemsArray.filter(
			(item) =>
				item.sellingPlanAllocation ||
				item.merchandise.sku.startsWith('bb-box') ||
				item.merchandise.sku.startsWith('signature-box')
		)

		async function performRevalidateAndOnAddToCart(
			revalidateFn: AsyncFunction,
			onAddToCartFn?: () => void
		): Promise<void> {
			try {
				await revalidateFn()
				onAddToCartFn?.()
			} catch (error) {
				console.error('Error during revalidate or onAddToCart:', error)
			}
		}

		async function clearCart(): Promise<void> {
			if (isCustomizableBox) {
				if (nonSubscriptionItems.length > 0) {
					await removeItems(
						nonSubscriptionItems
							.filter((item) => item.id !== undefined)
							.map((item) => ({ id: item.id as string }))
					)
				}

				if (customizableBoxInCartLineId) {
					await updateItem({
						id: customizableBoxInCartLineId,
						quantity: 1,
						variantId: variant.id as string,
						sellingPlanId: sellingPlanId || undefined
					})
				} else {
					await addItem({
						variantId: variant.id || '',
						quantity: 1,
						sellingPlanId
					})
				}
			} else if (!isCustomizableBox) {
				if (customizableBoxInCartLineId) {
					await removeItems(subscriptionItems.map((item) => ({ id: item.id || '' })))
				}
				await addItem({
					// @ts-ignore
					variantId: variant?.id,
					quantity: 1,
					sellingPlanId
				})
			}
			await performRevalidateAndOnAddToCart(revalidate, onAddToCart)
			setLoading(false)
		}

		clearCart()

		// Analytics ***
		const gtmEcommerceProduct = getGtmEcommerceProduct({
			product,
			price: variant?.price?.amount || product?.price?.amount,
			productBreadcrumb: breadcrumbs,
			isSubscriptionBox: !!isCustomizableBox
		})

		gtmEvent({
			event: 'analytics_event',
			data: {
				eventData: {
					category: 'ecommerce',
					action: 'addToCart',
					label: product?.name
				},
				ecommerce: {
					currencyCode: variant?.price?.currencyCode || product?.price?.currencyCode,
					add: {
						actionField: { list: breadcrumbs?.[0]?.title || 'membership' },
						products: [gtmEcommerceProduct]
					}
				}
			}
		})

		// Elevar Event
		if (product && product.id && variant && variant.id) {
			// Create a more complete product object for Elevar tracking
			const productForTracking = {
				...product,
				type: 'subscription',
				images: [],
				variants: [variant],
				price: product.price || variant.price,
				vendor: product.vendor || 'Birchbox',
				description: '',
				descriptionHtml: '',
				tags: [],
				sku: '',
				availableForSale: product.availableForSale || true,
				slug: '',
				path: window.location.pathname,
				handle: '',
				options: [],
				requiresSellingPlan: product.requiresSellingPlan || true,
				priceRange: {
					minVariantPrice: product.price || variant.price,
					maxVariantPrice: product.price || variant.price
				},
				sellingPlanGroups: [],
				collections: [],
				videos: null,
				notForSale: false,
				howToUse: null,
				brand: null,
				promotionalDescription: null,
				highlights: null,
				ingredients: [],
				avgRating: null,
				numReviews: null
			}
			
			pushAddToCart(productForTracking as ProductType, 1, 'Subscription Products')
		}

		// PostHog: add_to_cart (for experiments / funnel metrics)
		const priceAmount = variant?.price?.amount ?? product?.price?.amount
		posthog.capture('add_to_cart', {
			product_id: product?.id,
			product_name: product?.name,
			variant_id: variant?.id,
			quantity: 1,
			value: priceAmount != null ? parseFloat(String(priceAmount)) : undefined,
			currency: variant?.price?.currencyCode || product?.price?.currencyCode,
			is_subscription_box: !!isCustomizableBox,
			list: breadcrumbs?.[0]?.title || 'Subscription Products'
		})
		// Analytics ***
	}

	if (variant?.price && variant.price?.amount === '0') {
		return null
	}

	return (
		<button className={className} type="button" disabled={loading} onClick={addToCart}>
			{icon && <i className={icon} />}
			{productCTAText} {priceText && <span className="md:hidden">({priceText})</span>}
		</button>
	)
}
